import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { io } from 'socket.io-client';
import { getSecureFileUrl } from '../../utils/secureFileUtils';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL === '/api' ? '' : API_URL;
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:5000');

const CARD_DURATION_SECONDS = 10;

export interface PaymentApplicantInfo {
  applicantName: string;
  applicationId: string;
  amount: number;
  photo?: string | null;
  locationArea?: string;
  locationDistrict?: string;
  paidAt?: string; // ISO timestamp — when the payment actually occurred
  _id?: string; // notification DB id for acknowledgment
}

interface ConfettiPiece {
  id: number;
  x: number;
  y: number;
  color: string;
  size: number;
  rotation: number;
  rotationSpeed: number;
  speedX: number;
  speedY: number;
  gravity: number;
  opacity: number;
  shape: 'rect' | 'circle' | 'strip';
  fading: boolean;
}

interface PaymentConfettiProps {
  testTrigger?: number;
  testApplicantInfo?: PaymentApplicantInfo | null;
}

const COLORS = [
  '#6A0DAD', '#9B59B6', '#FF6B6B', '#4ECDC4', '#45B7D1',
  '#FFD93D', '#FF8C42', '#98D8C8', '#F7DC6F', '#BB8FCE',
  '#85C1E9', '#82E0AA', '#F1948A', '#D7BDE2', '#AED6F1',
];

// Animations
const cardSlideIn = keyframes`
  0% { transform: translate(-50%, -50%) scale(0.3); opacity: 0; }
  50% { transform: translate(-50%, -50%) scale(1.05); opacity: 1; }
  100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
`;

const cardSlideOut = keyframes`
  0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
  100% { transform: translate(-50%, -50%) scale(0.3) translateY(40px); opacity: 0; }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 30px rgba(106, 13, 173, 0.3), 0 20px 60px rgba(0,0,0,0.2); }
  50% { box-shadow: 0 0 50px rgba(106, 13, 173, 0.5), 0 20px 60px rgba(0,0,0,0.3); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

const InfoCard = styled.div<{ $isExiting: boolean }>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 100000;
  background: linear-gradient(145deg, #ffffff 0%, #faf5ff 40%, #f3e8ff 100%);
  border-radius: 24px;
  padding: 32px 36px;
  min-width: 380px;
  max-width: 440px;
  text-align: center;
  animation: ${props => props.$isExiting ? cardSlideOut : cardSlideIn} ${props => props.$isExiting ? '0.4s' : '0.6s'} cubic-bezier(0.16, 1, 0.3, 1) forwards,
    ${props => !props.$isExiting ? glow : 'none'} 2s ease-in-out infinite;
  border: 2px solid rgba(106, 13, 173, 0.15);
  pointer-events: auto;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    border-radius: 24px 24px 0 0;
    background: linear-gradient(90deg, #6A0DAD, #9B59B6, #c084fc, #6A0DAD);
    background-size: 200% auto;
    animation: ${shimmer} 2s linear infinite;
  }
`;

const CelebrationEmoji = styled.div`
  font-size: 2.2rem;
  margin-bottom: 8px;
  animation: ${float} 1.5s ease-in-out infinite;
`;

const PhotoContainer = styled.div`
  width: 90px;
  height: 90px;
  border-radius: 50%;
  margin: 0 auto 14px;
  overflow: hidden;
  border: 3px solid rgba(106, 13, 173, 0.25);
  box-shadow: 0 4px 16px rgba(106, 13, 173, 0.2);
  background: linear-gradient(135deg, #e9d5ff, #f3e8ff);
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PhotoPlaceholder = styled.div`
  font-size: 2rem;
  color: #9B59B6;
`;

const ApplicantName = styled.h3`
  margin: 0 0 4px;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e1b4b;
`;

const PaymentBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  font-size: 0.95rem;
  font-weight: 700;
  padding: 6px 16px;
  border-radius: 20px;
  margin: 10px 0 12px;
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
`;

const AreaInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 0.82rem;
  color: #6A0DAD;
  font-weight: 500;
  background: rgba(106, 13, 173, 0.06);
  padding: 8px 14px;
  border-radius: 10px;
  margin-top: 6px;
`;

const AppId = styled.div`
  font-size: 0.72rem;
  color: #94a3b8;
  margin-top: 10px;
  font-family: monospace;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(106, 13, 173, 0.08);
  border: none;
  color: #9B59B6;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  padding: 0;

  &:hover {
    background: rgba(106, 13, 173, 0.18);
    color: #6A0DAD;
  }
`;

const QueueBadge = styled.div`
  position: absolute;
  bottom: 10px;
  right: 12px;
  font-size: 0.65rem;
  color: #94a3b8;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
`;

// Circular countdown timer component (top-left of card)
const CountdownTimer: React.FC<{ seconds: number; total: number }> = ({ seconds, total }) => {
  const size = 32;
  const strokeWidth = 3;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = seconds / total;
  const offset = circumference * (1 - progress);

  return (
    <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }} title={`Auto-dismiss in ${seconds}s`}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(106, 13, 173, 0.08)" strokeWidth={strokeWidth} />
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke={seconds <= 3 ? '#ef4444' : '#9B59B6'}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.3s linear, stroke 0.3s ease' }}
        />
      </svg>
      <span style={{
        position: 'absolute',
        fontSize: '0.65rem',
        fontWeight: 700,
        color: seconds <= 3 ? '#ef4444' : '#7c3aed',
        fontVariantNumeric: 'tabular-nums',
        transition: 'color 0.3s ease',
      }}>{seconds}</span>
    </div>
  );
};

const PaymentConfetti: React.FC<PaymentConfettiProps> = ({ testTrigger, testApplicantInfo }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const piecesRef = useRef<ConfettiPiece[]>([]);
  const animFrameRef = useRef<number>(0);
  const confettiActiveRef = useRef(false);
  const spawnActiveRef = useRef(false);
  const spawnIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const nextPieceIdRef = useRef(0);

  const [showCard, setShowCard] = useState(false);
  const [cardExiting, setCardExiting] = useState(false);
  const [applicantInfo, setApplicantInfo] = useState<PaymentApplicantInfo | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(CARD_DURATION_SECONDS);
  const [canvasVisible, setCanvasVisible] = useState(false);

  const cardTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const lastPaymentIdRef = useRef<string | null>(null);
  const initialFetchDoneRef = useRef(false);

  // Event queue for multiple simultaneous payments
  const queueRef = useRef<PaymentApplicantInfo[]>([]);
  const showingRef = useRef(false);
  const [queueLength, setQueueLength] = useState(0);

  // Create a batch of confetti pieces
  const createBatch = useCallback((count: number): ConfettiPiece[] => {
    const pieces: ConfettiPiece[] = [];
    const shapes: Array<'rect' | 'circle' | 'strip'> = ['rect', 'circle', 'strip'];
    for (let i = 0; i < count; i++) {
      pieces.push({
        id: nextPieceIdRef.current++,
        x: Math.random() * window.innerWidth,
        y: -20 - Math.random() * 200,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        size: 4 + Math.random() * 8,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 10,
        speedX: (Math.random() - 0.5) * 4,
        speedY: 1.5 + Math.random() * 3,
        gravity: 0.02 + Math.random() * 0.02,
        opacity: 1,
        shape: shapes[Math.floor(Math.random() * shapes.length)],
        fading: false,
      });
    }
    return pieces;
  }, []);

  // Animation loop — runs as long as there are pieces alive
  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    let alive = 0;

    piecesRef.current.forEach(p => {
      if (p.opacity <= 0) return;
      alive++;

      p.x += p.speedX;
      p.speedY += p.gravity;
      p.y += p.speedY;
      p.rotation += p.rotationSpeed;

      // Fade out when past 70% of screen or when flagged
      if (p.y > canvas.height * 0.7 || p.fading) {
        p.opacity -= 0.02;
        p.fading = true;
      }

      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate((p.rotation * Math.PI) / 180);
      ctx.globalAlpha = Math.max(0, p.opacity);
      ctx.fillStyle = p.color;

      if (p.shape === 'rect') {
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
      } else if (p.shape === 'circle') {
        ctx.beginPath();
        ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillRect(-p.size / 2, -1, p.size, 3);
      }
      ctx.restore();
    });

    // Prune dead pieces periodically to avoid memory growth
    if (piecesRef.current.length > 600) {
      piecesRef.current = piecesRef.current.filter(p => p.opacity > 0);
    }

    if (alive > 0 || spawnActiveRef.current) {
      confettiActiveRef.current = true;
      animFrameRef.current = requestAnimationFrame(animate);
    } else {
      confettiActiveRef.current = false;
      setCanvasVisible(false);
    }
  }, []);

  // Start continuous confetti spawning
  const startConfetti = useCallback(() => {
    // Initial big burst
    piecesRef.current = createBatch(150);
    spawnActiveRef.current = true;
    setCanvasVisible(true);

    if (!confettiActiveRef.current) {
      confettiActiveRef.current = true;
      animFrameRef.current = requestAnimationFrame(animate);
    }

    // Continuous smaller waves every 400ms
    if (spawnIntervalRef.current) clearInterval(spawnIntervalRef.current);
    spawnIntervalRef.current = setInterval(() => {
      if (spawnActiveRef.current) {
        piecesRef.current.push(...createBatch(20));
      }
    }, 400);
  }, [createBatch, animate]);

  // Stop spawning new confetti; existing pieces fade naturally
  const stopConfetti = useCallback(() => {
    spawnActiveRef.current = false;
    if (spawnIntervalRef.current) {
      clearInterval(spawnIntervalRef.current);
      spawnIntervalRef.current = null;
    }
    // Flag all remaining pieces to fade out
    piecesRef.current.forEach(p => { p.fading = true; });
  }, []);

  // Show the next event from the queue
  const showNextFromQueue = useCallback(() => {
    if (queueRef.current.length === 0) {
      showingRef.current = false;
      setQueueLength(0);
      return;
    }

    showingRef.current = true;
    const info = queueRef.current.shift()!;
    setQueueLength(queueRef.current.length);

    // Set applicant info and show card
    setApplicantInfo(info);
    setPhotoUrl(null);
    setCardExiting(false);
    setShowCard(true);
    setCountdown(CARD_DURATION_SECONDS);

    // Start confetti
    startConfetti();

    // Resolve photo
    if (info.photo) {
      getSecureFileUrl(info.photo, info.applicationId).then(url => {
        if (url) setPhotoUrl(url);
      });
    }

    // Countdown timer (updates every second)
    let remaining = CARD_DURATION_SECONDS;
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      remaining--;
      setCountdown(remaining);
      if (remaining <= 0) {
        if (countdownRef.current) clearInterval(countdownRef.current);
      }
    }, 1000);

    // Auto-dismiss after CARD_DURATION_SECONDS
    if (cardTimerRef.current) clearTimeout(cardTimerRef.current);
    cardTimerRef.current = setTimeout(() => {
      dismissCard();
    }, CARD_DURATION_SECONDS * 1000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startConfetti]);

  // Dismiss current card, then show next queued item
  const dismissCard = useCallback(() => {
    if (cardTimerRef.current) clearTimeout(cardTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);

    // Stop spawning confetti
    stopConfetti();

    setCardExiting(true);
    setTimeout(() => {
      setShowCard(false);
      setCardExiting(false);
      setApplicantInfo(null);
      setPhotoUrl(null);
      setCountdown(CARD_DURATION_SECONDS);

      // Show next queued event after a brief pause
      setTimeout(() => {
        showNextFromQueue();
      }, 500);
    }, 400);
  }, [stopConfetti, showNextFromQueue]);

  // Enqueue a payment event
  const enqueuePayment = useCallback((info: PaymentApplicantInfo) => {
    // Deduplicate by applicationId
    const isDup = queueRef.current.some(q => q.applicationId === info.applicationId) ||
      (applicantInfo?.applicationId === info.applicationId && showingRef.current);
    if (isDup) return;

    if (!showingRef.current) {
      // Nothing showing — display immediately
      queueRef.current.push(info);
      setQueueLength(queueRef.current.length);
      showNextFromQueue();
    } else {
      // Something is showing — add to queue
      queueRef.current.push(info);
      setQueueLength(queueRef.current.length);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNextFromQueue]);

  // Poll for new payments
  useEffect(() => {
    const checkPayment = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/admin/payment-stats`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.lastPayment) {
            const currentId = data.lastPayment.applicationId;
            if (initialFetchDoneRef.current && lastPaymentIdRef.current && currentId !== lastPaymentIdRef.current) {
              enqueuePayment({
                applicantName: data.lastPayment.applicantName,
                applicationId: data.lastPayment.applicationId,
                amount: data.lastPayment.amount,
                photo: data.lastPayment.photo,
                locationArea: data.lastPayment.locationArea,
                locationDistrict: data.lastPayment.locationDistrict,
              });
            }
            lastPaymentIdRef.current = currentId;
            initialFetchDoneRef.current = true;
          }
        }
      } catch {
        // silent
      }
    };

    checkPayment();
    const interval = setInterval(checkPayment, 5000);
    return () => clearInterval(interval);
  }, [enqueuePayment]);

  // Socket listener
  useEffect(() => {
    const socket = io(SOCKET_URL, { withCredentials: true, transports: ['websocket', 'polling'] });

    socket.on('payment-received', (data: any) => {
      if (data) enqueuePayment(data);
    });

    return () => {
      socket.off('payment-received');
      socket.disconnect();
    };
  }, [enqueuePayment]);

  // Fetch pending payment notifications on mount (for offline admins)
  const pendingFetchedRef = useRef(false);
  useEffect(() => {
    if (pendingFetchedRef.current) return;
    pendingFetchedRef.current = true;

    const fetchPending = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/admin/payment-notifications/pending`, {
          credentials: 'include',
        });
        if (!response.ok) return;
        const data = await response.json();
        if (data.success && data.notifications && data.notifications.length > 0) {
          const ids: string[] = [];
          for (const n of data.notifications) {
            ids.push(n._id);
            enqueuePayment({
              applicantName: n.applicantName,
              applicationId: n.applicationId,
              amount: n.amount,
              photo: n.photo,
              locationArea: n.locationArea,
              locationDistrict: n.locationDistrict,
              paidAt: n.paidAt || n.createdAt,
              _id: n._id,
            });
          }
          // Acknowledge so they don't show again
          fetch(`${API_BASE}/api/admin/payment-notifications/acknowledge`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ notificationIds: ids }),
          }).catch(() => {}); // fire and forget
        }
      } catch {
        // silent
      }
    };

    // Small delay so the initial payment-stats fetch sets the lastPaymentIdRef first
    const timer = setTimeout(fetchPending, 3000);
    return () => clearTimeout(timer);
  }, [enqueuePayment]);

  // Test trigger from parent
  useEffect(() => {
    if (testTrigger && testTrigger > 0) {
      enqueuePayment(testApplicantInfo || {
        applicantName: 'Test Applicant',
        applicationId: `CNG-TEST-${Date.now()}`,
        amount: 5000,
        photo: null,
        locationArea: 'Pune GA',
        locationDistrict: 'Pune',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testTrigger]);

  // ESC key to dismiss card
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showCard) dismissCard();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showCard, dismissCard]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (cardTimerRef.current) clearTimeout(cardTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (spawnIntervalRef.current) clearInterval(spawnIntervalRef.current);
    };
  }, []);

  if (!canvasVisible && !showCard) return null;

  const areaText = applicantInfo?.locationArea
    ? `${applicantInfo.locationArea}${applicantInfo.locationDistrict ? `, ${applicantInfo.locationDistrict}` : ''}`
    : applicantInfo?.locationDistrict || '';

  return (
    <>
      {canvasVisible && (
        <canvas
          ref={canvasRef}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            pointerEvents: 'none',
            zIndex: 99999,
          }}
        />
      )}
      {showCard && applicantInfo && (
        <InfoCard $isExiting={cardExiting}>
          <CountdownTimer seconds={countdown} total={CARD_DURATION_SECONDS} />
          <CloseBtn onClick={dismissCard} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </CloseBtn>
          <CelebrationEmoji>🎉</CelebrationEmoji>
          <PhotoContainer>
            {photoUrl ? (
              <img src={photoUrl} alt={applicantInfo.applicantName} onError={() => setPhotoUrl(null)} />
            ) : (
              <PhotoPlaceholder>
                {applicantInfo.applicantName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
              </PhotoPlaceholder>
            )}
          </PhotoContainer>
          <ApplicantName>{applicantInfo.applicantName}</ApplicantName>
          <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: 2 }}>Payment Successful!</div>
          {applicantInfo.paidAt && (() => {
            const paidDate = new Date(applicantInfo.paidAt!);
            const now = new Date();
            const diffMs = now.getTime() - paidDate.getTime();
            // Only show timestamp if payment is older than 2 minutes (i.e. missed/queued)
            if (diffMs > 2 * 60 * 1000) {
              const timeStr = paidDate.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
              const mins = Math.floor(diffMs / 60000);
              const agoStr = mins < 60 ? `${mins}m ago` : mins < 1440 ? `${Math.floor(mins / 60)}h ${mins % 60}m ago` : `${Math.floor(mins / 1440)}d ago`;
              return (
                <div style={{
                  fontSize: '0.68rem', color: '#f59e0b', fontWeight: 600,
                  background: '#fefce8', border: '1px solid #fde68a',
                  borderRadius: 8, padding: '4px 10px', marginBottom: 4,
                  display: 'flex', alignItems: 'center', gap: 4,
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                  Paid on {timeStr} ({agoStr})
                </div>
              );
            }
            return null;
          })()}
          <PaymentBadge>
            <span>₹</span>
            <span>{(applicantInfo.amount || 5000).toLocaleString('en-IN')}</span>
          </PaymentBadge>
          {areaText && (
            <AreaInfo>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              CNG Dealership — {areaText}
            </AreaInfo>
          )}
          <AppId>{applicantInfo.applicationId}</AppId>
          {queueLength > 0 && (
            <QueueBadge>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 6v6l4 2"/><circle cx="12" cy="12" r="10"/></svg>
              +{queueLength} more payment{queueLength > 1 ? 's' : ''} queued
            </QueueBadge>
          )}
        </InfoCard>
      )}
    </>
  );
};

export default PaymentConfetti;
